import { CLIENT_ID } from "@api/client-id";
import { runtimeConfig } from "@src/runtime-config";
import { AnalyticsApiClient } from "@stellar/analytics-api-client";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { sentryCaptureError } from "@utils/sentry-utils";

/**
 * Create Basic Authorization header.
 *
 * Credentials stored in local storage (set via the dev features dialog) take precedence over the
 * runtime config values. This is a temporary solution until JWT auth is implemented.
 * TODO: The logic will be replaced with JWT auth once implemented: https://faro01.atlassian.net/browse/ST-4680
 */
function createBasicAuthHeader(): string | null {
  const username = LocalStorageUtils.getStringItem("analytics-api-username");
  const password = LocalStorageUtils.getStringItem("analytics-api-password");

  if (!username || !password) {
    return null;
  }
  const credentials = `${username}:${password}`;
  const encoded = btoa(credentials);
  return `Basic ${encoded}`;
}

/**
 * This is the new Analytics API client that is used to communicate with the Analytics API repo.
 * It is used to fetch data from the Analytics API and to send data to the Analytics API.
 * Currently this is only working on com-dev
 */
const authHeader = createBasicAuthHeader();
const headers: Record<string, string> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "X-Holobuilder-Component": CLIENT_ID,
};
if (authHeader) {
  headers.Authorization = authHeader;
}

export const analyticsApiClient = new AnalyticsApiClient({
  baseUrl: runtimeConfig.urls.analyticsBaseUrl,
  headers,
  // If the return type is different than what we expected, we log an assertion error.
  onLogAssertion: ({ message, error, routeName }) => {
    const title = `AnalyticsApiClient assertion error in route ${routeName}.`;

    // Capture the error with Sentry. The title has the route name making it easy to find.
    // The error shows the message from Zod as it is human readable
    sentryCaptureError({
      title,
      error: message,
      isAssertionError: true,
    });

    // eslint-disable-next-line no-console
    console.error(title, message, error.issues);
  },
});
